import React from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  MessageCircle,
  Send,
  Heart,
} from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useT } from '../contexts/LanguageContext';

export const Footer = () => {
  const settings = useSiteSettings();
  const tr = useT();
  const logoUrl = settings.logo_url || '/logo.jpg';

  const contactEmail = settings.contact_email || 'info@rishidevfoundation.in';
  const phone = settings.phone_primary || '+91 98959 59558';
  const phoneTel = phone.replace(/[^+\d]/g, '');

  // Multi-line address strings (fall back to user-provided defaults on first load)
  const registeredAddress =
    settings.registered_address ||
    '35/1623, 2nd Floor, Jubilee Bazar, Ondane Road,\nKannur-1, Kerala, India, 670001';
  const adminAddress =
    settings.admin_address ||
    'Vins Villa, Kadungallur Road, Thottakkattukara,\nAluva, Ernakulam, Kerala – 683108, India';

  const socials = [
    { key: 'whatsapp', url: settings.whatsapp ? `https://wa.me/${settings.whatsapp}` : null, icon: <MessageCircle size={18} />, hoverBg: '#25D366', label: 'WhatsApp' },
    { key: 'telegram', url: settings.telegram ? `https://t.me/${settings.telegram}` : null, icon: <Send size={18} />, hoverBg: '#0088CC', label: 'Telegram' },
    { key: 'facebook', url: settings.facebook, icon: <Facebook size={18} />, hoverBg: '#1877F2', label: 'Facebook' },
    { key: 'instagram', url: settings.instagram, icon: <Instagram size={18} />, hoverBg: '#E1306C', label: 'Instagram' },
    { key: 'youtube', url: settings.youtube, icon: <Youtube size={18} />, hoverBg: '#FF0000', label: 'YouTube' },
    { key: 'twitter', url: settings.twitter, icon: <Twitter size={18} />, hoverBg: '#000000', label: 'Twitter/X' },
    { key: 'linkedin', url: settings.linkedin, icon: <Linkedin size={18} />, hoverBg: '#0A66C2', label: 'LinkedIn' },
  ].filter((s) => s.url);

  return (
    <footer className="bg-charcoal-900 text-charcoal-100 pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img src={logoUrl} alt="Rishidev Foundation" className="h-14 w-14 rounded-full object-cover shadow-lg" />
              <div className="flex flex-col leading-tight" style={{ color: '#a37f3f' }}>
                <span className="font-brand text-lg" style={{ fontWeight: 800 }}>Rishidev</span>
                <span className="font-brand text-lg" style={{ fontWeight: 700 }}>Foundation</span>
              </div>
            </div>
            <p className="text-sm text-charcoal-100/70 leading-relaxed">
              {tr('A sacred space dedicated to preserving and sharing the eternal wisdom of Sanatana Dharma.')}
            </p>
            {settings.tax_exemption_number && (
              <p className="text-xs text-charcoal-100/50 mt-3">
                {tr('80(G) Exempted')} • {settings.tax_exemption_number}
              </p>
            )}
          </div>

          <div>
            <h3 className="font-heading text-lg text-saffron-400 mb-4">{tr('Explore')}</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors">{tr('About Us')}</Link></li>
              <li><Link to="/mentor" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors">{tr('Our Mentor')}</Link></li>
              <li><Link to="/courses" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors">{tr('Courses')}</Link></li>
              <li><Link to="/events" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors">{tr('Events')}</Link></li>
              <li><Link to="/gallery" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors">{tr('Gallery')}</Link></li>
              <li><Link to="/donate" className="text-charcoal-100/70 hover:text-saffron-400 transition-colors flex items-center gap-1"><Heart size={14} className="text-saffron-400" /> {tr('Donate')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg text-saffron-400 mb-4">{tr('Contact')}</h3>
            <ul className="space-y-3 text-sm text-charcoal-100/70">
              <li>
                <a href={`mailto:${contactEmail}`} className="flex items-start gap-2 hover:text-saffron-400 transition-colors">
                  <Mail size={16} className="mt-1 flex-shrink-0" />
                  <span className="break-all">{contactEmail}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${phoneTel}`} className="flex items-start gap-2 hover:text-saffron-400 transition-colors">
                  <Phone size={16} className="mt-1 flex-shrink-0" />
                  <span>{phone}</span>
                </a>
              </li>
              <li className="flex items-start gap-2 pt-1" data-testid="footer-registered-address">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-saffron-400/80 font-semibold mb-1">
                    {tr('Registered Office')}
                  </div>
                  <p className="whitespace-pre-line leading-relaxed">{tr(registeredAddress)}</p>
                </div>
              </li>
              <li className="flex items-start gap-2" data-testid="footer-admin-address">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-saffron-400/80 font-semibold mb-1">
                    {tr('Administrative Office')}
                  </div>
                  <p className="whitespace-pre-line leading-relaxed">{tr(adminAddress)}</p>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg text-saffron-400 mb-4">{tr('Connect')}</h3>
            <p className="text-sm text-charcoal-100/70 mb-4">{tr('Reach us on your favorite platform.')}</p>
            <div className="flex flex-wrap gap-3">
              {socials.length === 0 ? (
                <p className="text-xs text-charcoal-100/50 italic">{tr('No social links set. Configure in Admin → Settings.')}</p>
              ) : (
                socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-charcoal-800 flex items-center justify-center transition-colors"
                    style={{ transition: 'background-color 0.2s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = s.hoverBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                    aria-label={s.label}
                    data-testid={`footer-${s.key}`}
                  >
                    {s.icon}
                  </a>
                ))
              )}
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg text-saffron-400 mb-4">{tr('Legal')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/terms-and-conditions"
                  data-testid="footer-terms-link"
                  className="text-charcoal-100/70 hover:text-saffron-400 transition-colors"
                >
                  {tr('Terms & Conditions')}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid="footer-privacy-link"
                  className="text-charcoal-100/70 hover:text-saffron-400 transition-colors"
                >
                  {tr('Privacy Policy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  data-testid="footer-refund-link"
                  className="text-charcoal-100/70 hover:text-saffron-400 transition-colors"
                >
                  {tr('Refund Policy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/cancellation-policy"
                  data-testid="footer-cancellation-link"
                  className="text-charcoal-100/70 hover:text-saffron-400 transition-colors"
                >
                  {tr('Cancellation Policy')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-charcoal-800 pt-8 text-center text-sm text-charcoal-100/60">
          <p className="mb-2 font-heading italic text-base">आत्मनो मोक्षार्थं जगद्धिताय च</p>
          <p className="text-xs">{tr('For the liberation of the self, and for the welfare of the world')}</p>
          <p className="mt-4">© {new Date().getFullYear()} {tr('Rishidev Foundation. All rights reserved.')}</p>
        </div>
      </div>
    </footer>
  );
};
